import { Helmet } from 'react-helmet-async';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';
const CONTACT_EMAIL = 'contact@criptonews.site';
const RESPONSIBLE_NAME = 'Gabriel Leite Moreira';
const RESPONSIBLE_LOCATION = 'Blumenau, SC, Brasil';
const LAST_UPDATED = '9 de maio de 2026';

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Política de Privacidade | {SITE_NAME}</title>
        <meta
          name="description"
          content={`Política de Privacidade do ${SITE_NAME}. Saiba como coletamos, usamos e protegemos seus dados em conformidade com a LGPD.`}
        />
        <link rel="canonical" href={`${SITE_URL}/politica-de-privacidade`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-3xl">
          <div className="bg-white rounded-lg shadow p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Política de Privacidade</h1>
            <p className="text-sm text-gray-500 mb-8">Última atualização: {LAST_UPDATED}</p>

            <p className="text-gray-700 mb-6">
              Esta Política de Privacidade descreve como o <strong>{SITE_NAME}</strong> ("{SITE_URL}") coleta,
              usa e compartilha informações sobre você quando visita nosso site. Ao utilizar nosso site, você
              concorda com as práticas descritas nesta política, em conformidade com a Lei Geral de Proteção
              de Dados (LGPD — Lei nº 13.709/2018).
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">1. Informações que Coletamos</h2>
              <p className="text-gray-700 mb-3">
                Podemos coletar automaticamente os seguintes tipos de informação quando você visita nosso site:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Tipo e versão do navegador;</li>
                <li>Páginas visitadas e tempo gasto em cada página;</li>
                <li>Site de referência ou mecanismo de busca de origem;</li>
                <li>Tipo de dispositivo, sistema operacional e resolução de tela;</li>
                <li>Localização geográfica aproximada (nível país/cidade, baseada no endereço IP);</li>
                <li>Interações com o conteúdo (cliques, rolagens).</li>
              </ul>
              <p className="text-gray-700 mt-3">
                <strong>Não</strong> coletamos informações pessoalmente identificáveis como nome, e-mail ou
                telefone, exceto quando você as fornece voluntariamente por meio do formulário de contato.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">2. Cookies e Tecnologias de Rastreamento</h2>
              <p className="text-gray-700 mb-3">
                Nosso site utiliza cookies — pequenos arquivos de texto armazenados no seu navegador — para as
                seguintes finalidades:
              </p>

              <div className="overflow-x-auto mb-4">
                <table className="w-full text-sm text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-200 px-3 py-2 font-semibold text-gray-700">Cookie</th>
                      <th className="border border-gray-200 px-3 py-2 font-semibold text-gray-700">Finalidade</th>
                      <th className="border border-gray-200 px-3 py-2 font-semibold text-gray-700">Duração</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">Google Analytics (_ga, _gid)</td>
                      <td className="border border-gray-200 px-3 py-2">Análise de tráfego e comportamento dos usuários</td>
                      <td className="border border-gray-200 px-3 py-2">2 anos / 24h</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-3 py-2">Google AdSense</td>
                      <td className="border border-gray-200 px-3 py-2">Exibição de anúncios personalizados</td>
                      <td className="border border-gray-200 px-3 py-2">Variável</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-3 py-2">cookie_consent</td>
                      <td className="border border-gray-200 px-3 py-2">Armazenar sua preferência de consentimento</td>
                      <td className="border border-gray-200 px-3 py-2">1 ano</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-3">
                Você pode controlar ou desativar os cookies pelas configurações do seu navegador a qualquer momento.
                A desativação pode afetar algumas funcionalidades do site.
              </p>
              <p className="text-gray-700">
                Para gerenciar preferências de cookies de publicidade, acesse{' '}
                <a
                  href="https://www.google.com/settings/ads"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Configurações de Anúncios do Google
                </a>
                . Para desativar o rastreamento do Google Analytics, instale o{' '}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  complemento de desativação do Google Analytics
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">3. Google Analytics</h2>
              <p className="text-gray-700 mb-3">
                Utilizamos o Google Analytics para coletar informações anônimas sobre como os visitantes usam
                nosso site. Esses dados nos ajudam a melhorar o conteúdo e a experiência do usuário. O Google
                Analytics usa cookies para rastrear interações dos usuários.
              </p>
              <p className="text-gray-700">
                Os dados coletados pelo Google Analytics são processados de acordo com a{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Política de Privacidade do Google
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">4. Google AdSense</h2>
              <p className="text-gray-700 mb-3">
                Utilizamos o Google AdSense para exibir anúncios em nosso site. O Google AdSense pode usar
                cookies e web beacons para veicular anúncios com base em suas visitas anteriores ao nosso site
                ou a outros sites na internet.
              </p>
              <p className="text-gray-700">
                Você pode optar por não receber publicidade personalizada acessando as{' '}
                <a
                  href="https://www.google.com/settings/ads"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Configurações de Anúncios do Google
                </a>
                . Para mais informações sobre como o Google utiliza dados, consulte a{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Política de Privacidade do Google
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">5. Conteúdo de Terceiros</h2>
              <p className="text-gray-700">
                O {SITE_NAME} agrega resumos e manchetes de notícias de fontes publicamente disponíveis, incluindo
                CoinDesk, CoinTelegraph, Bitcoin.com e CryptoSlate. Todos os artigos incluem link direto para
                o original em seus respectivos sites. Não somos responsáveis pelas práticas de privacidade ou
                pelo conteúdo desses sites de terceiros.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">6. Retenção de Dados</h2>
              <p className="text-gray-700">
                Os dados analíticos coletados via Google Analytics são retidos de acordo com as políticas padrão
                de retenção de dados do Google. Não armazenamos dados pessoais em nossos próprios servidores além
                do necessário para o funcionamento do site.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">7. Seus Direitos (LGPD)</h2>
              <p className="text-gray-700 mb-3">
                Nos termos da Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018), você tem os
                seguintes direitos em relação aos seus dados pessoais:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Direito de acesso aos dados que mantemos sobre você;</li>
                <li>Direito de solicitar a correção ou exclusão dos seus dados;</li>
                <li>Direito de se opor ou restringir determinados tipos de processamento;</li>
                <li>Direito à portabilidade dos dados;</li>
                <li>Direito de revogar o consentimento a qualquer momento;</li>
                <li>Direito de peticionar à Autoridade Nacional de Proteção de Dados (ANPD).</li>
              </ul>
              <p className="text-gray-700 mt-3">
                Para exercer qualquer um desses direitos, entre em contato pelo e-mail{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">8. Alterações nesta Política</h2>
              <p className="text-gray-700">
                Podemos atualizar esta Política de Privacidade periodicamente. As alterações serão publicadas
                nesta página com a atualização da data de "Última atualização". Recomendamos que você revise
                esta página periodicamente.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">9. Controlador de Dados e Contato</h2>
              <p className="text-gray-700 mb-4">
                O controlador dos dados pessoais tratados neste site é:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-gray-700 text-sm space-y-1">
                <p><strong>Site:</strong> {SITE_NAME}</p>
                <p><strong>Responsável:</strong> {RESPONSIBLE_NAME}</p>
                <p><strong>Localização:</strong> {RESPONSIBLE_LOCATION}</p>
                <p>
                  <strong>E-mail:</strong>{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">
                    {CONTACT_EMAIL}
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};
